import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';
import { CheckCircle2, XCircle, RefreshCw } from 'lucide-react';

interface Contraste {
  candidato_id: number;
  dni: string;
  reniec_cacheado: boolean;
  reniec_cached_at: string | null;
  todo_ok: boolean;
  inconsistencias: string[];
  campos: { campo: string; candidato: any; reniec: any; ok: boolean }[];
}

export function ContrasteDniBox() {
  const { data, isLoading, refetch, isFetching } = useQuery<Contraste>({
    queryKey: ['contraste-dni'],
    queryFn: async () => (await api.get<Contraste>('/me/dni/contraste')).data,
  });

  if (isLoading || !data) return <div className="text-xs text-gray-500">Cargando contraste…</div>;

  return (
    <div className={`border-2 rounded-lg p-4 ${
      data.todo_ok
        ? 'bg-green-50 border-green-300'
        : 'bg-amber-50 border-amber-300'
    }`}>
      <header className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {data.todo_ok ? (
            <CheckCircle2 size={20} className="text-green-600" />
          ) : (
            <XCircle size={20} className="text-amber-700" />
          )}
          <h4 className="text-sm font-semibold">
            {data.todo_ok
              ? '✅ Tus datos coinciden con RENIEC'
              : `⚠ ${data.inconsistencias.length} inconsistencia${data.inconsistencias.length === 1 ? '' : 's'} con RENIEC`}
          </h4>
        </div>
        <button
          onClick={() => refetch()}
          className="text-[11px] text-gray-600 hover:text-arpp-primary flex items-center gap-1"
          disabled={isFetching}
          title="Re-consultar"
        >
          <RefreshCw size={11} className={isFetching ? 'animate-spin' : ''} /> Refrescar
        </button>
      </header>

      <table className="w-full text-xs">
        <thead>
          <tr className="text-gray-600 border-b">
            <th className="text-left py-1">Campo</th>
            <th className="text-left py-1">En el sistema</th>
            <th className="text-left py-1">En RENIEC</th>
            <th className="text-center py-1 w-12">OK</th>
          </tr>
        </thead>
        <tbody>
          {data.campos.map((f) => (
            <tr key={f.campo} className={`border-b last:border-b-0 ${!f.ok ? 'bg-amber-100/50' : ''}`}>
              <td className="py-1 font-medium">{f.campo}</td>
              <td className="py-1 font-mono">{f.candidato ?? '—'}</td>
              <td className="py-1 font-mono">{f.reniec ?? '—'}</td>
              <td className="py-1 text-center">
                {f.ok ? (
                  <CheckCircle2 size={14} className="inline text-green-600" />
                ) : (
                  <XCircle size={14} className="inline text-red-600" />
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {!data.todo_ok && (
        <div className="mt-3 text-[11px] text-amber-900 bg-amber-100 border border-amber-300 rounded p-2">
          Si los datos en el sistema NO coinciden con tu DNI físico, contacta al
          Personero Legal Titular (PLT). El sistema confía en los datos RENIEC para
          generar tus documentos ante el JEE.
        </div>
      )}

      <div className="mt-2 text-[10px] text-gray-500">
        RENIEC cacheado: {data.reniec_cached_at ? new Date(data.reniec_cached_at).toLocaleString('es-PE') : 'no disponible'}
      </div>
    </div>
  );
}
